import { useState, useEffect, useCallback } from 'react'
import MobileLayout from '../../components/MobileLayout'
import { employeeApi } from '../../lib/employeeApi'
import { MapPin, WifiOff, CheckCircle } from 'lucide-react'

interface ClockEvent {
  id: string
  eventType: string
  recordedAt: string
  note?: string
  latitude?: number | null
  longitude?: number | null
  adjustedTime?: string | null
}

// File hors-ligne stockée dans localStorage
interface OfflineEvent {
  id: string
  eventType: string
  note?: string
  latitude?: number
  longitude?: number
  offlineRecordedAt: string
}

const OFFLINE_QUEUE_KEY = 'clock_offline_queue'

const EVENT_LABELS: Record<string, string> = {
  arrive: 'Arrivée',
  break:  'Début pause',
  resume: 'Fin de pause',
  leave:  'Départ'
}

const EVENT_ICONS: Record<string, string> = {
  arrive: '🟢',
  break:  '🟡',
  resume: '🔵',
  leave:  '🔴'
}

function formatTime(iso: string) {
  return new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
}

function getNextAction(events: ClockEvent[]): string | null {
  if (events.length === 0) return 'arrive'
  const last = events[events.length - 1].eventType
  if (last === 'arrive' || last === 'resume') return 'break'
  if (last === 'break') return 'resume'
  if (last === 'leave') return null
  return 'leave'
}

function loadOfflineQueue(): OfflineEvent[] {
  try { return JSON.parse(localStorage.getItem(OFFLINE_QUEUE_KEY) || '[]') } catch { return [] }
}

function saveOfflineQueue(q: OfflineEvent[]) {
  localStorage.setItem(OFFLINE_QUEUE_KEY, JSON.stringify(q))
}

async function getGeolocation(): Promise<{ latitude: number; longitude: number } | null> {
  if (!navigator.geolocation) return null
  return new Promise(resolve => {
    navigator.geolocation.getCurrentPosition(
      pos => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
      () => resolve(null),
      { timeout: 3000, maximumAge: 60000 }
    )
  })
}

export default function MobileClock() {
  const [events, setEvents] = useState<ClockEvent[]>([])
  const [loading, setLoading] = useState(true)
  const [posting, setPosting] = useState(false)
  const [note, setNote] = useState('')
  const [showNote, setShowNote] = useState(false)
  const [now, setNow] = useState(new Date())
  const [isOnline, setIsOnline] = useState(navigator.onLine)
  const [offlineQueue, setOfflineQueue] = useState<OfflineEvent[]>(loadOfflineQueue)
  const [syncingQueue, setSyncingQueue] = useState(false)
  const [geoEnabled, setGeoEnabled] = useState(false)
  const [lastGeo, setLastGeo] = useState<{ latitude: number; longitude: number } | null>(null)
  const [justPosted, setJustPosted] = useState<string | null>(null)

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000)
    const handleOnline = () => setIsOnline(true)
    const handleOffline = () => setIsOnline(false)
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
    return () => { clearInterval(t); window.removeEventListener('online', handleOnline); window.removeEventListener('offline', handleOffline) }
  }, [])

  useEffect(() => { loadToday() }, [])

  // Synchroniser la file hors-ligne quand on revient en ligne
  useEffect(() => {
    if (isOnline && offlineQueue.length > 0) {
      syncOfflineQueue()
    }
  }, [isOnline])

  async function loadToday() {
    setLoading(true)
    try {
      const r = await employeeApi.get('/employee/clock/today')
      setEvents(r.data)
    } catch { /* offline */ }
    setLoading(false)
  }

  async function syncOfflineQueue() {
    if (syncingQueue) return
    setSyncingQueue(true)
    const queue = loadOfflineQueue()
    const remaining: OfflineEvent[] = []
    for (const ev of queue) {
      try {
        await employeeApi.post('/employee/clock', {
          eventType: ev.eventType,
          note: ev.note,
          latitude: ev.latitude,
          longitude: ev.longitude,
          offlineRecordedAt: ev.offlineRecordedAt
        })
      } catch {
        remaining.push(ev)
      }
    }
    saveOfflineQueue(remaining)
    setOfflineQueue(remaining)
    setSyncingQueue(false)
    if (remaining.length < queue.length) await loadToday()
  }

  async function doEvent(type: string) {
    setPosting(true)
    const recordedAt = new Date().toISOString()
    let geo = null
    if (geoEnabled) {
      geo = await getGeolocation()
      if (geo) setLastGeo(geo)
    }

    try {
      if (isOnline) {
        await employeeApi.post('/employee/clock', {
          eventType: type,
          note: note || undefined,
          latitude: geo?.latitude,
          longitude: geo?.longitude,
          offlineRecordedAt: recordedAt
        })
        setJustPosted(type)
        setTimeout(() => setJustPosted(null), 3000)
        await loadToday()
      } else {
        // Mode hors-ligne : stocker dans la file locale
        const offlineEvent: OfflineEvent = {
          id: Math.random().toString(36).slice(2),
          eventType: type,
          note: note || undefined,
          latitude: geo?.latitude,
          longitude: geo?.longitude,
          offlineRecordedAt: recordedAt
        }
        const updated = [...loadOfflineQueue(), offlineEvent]
        saveOfflineQueue(updated)
        setOfflineQueue(updated)
        // Simuler localement pour l'UI
        setEvents(prev => [...prev, { id: offlineEvent.id, eventType: type, recordedAt, note }])
        setJustPosted(type)
        setTimeout(() => setJustPosted(null), 3000)
      }
      setNote('')
      setShowNote(false)
    } finally {
      setPosting(false)
    }
  }

  const nextAction = getNextAction(events)

  const ACTION_CONFIG: Record<string, { label: string; bg: string }> = {
    arrive: { label: 'Pointer l\'arrivée',    bg: 'bg-green-500 hover:bg-green-600' },
    break:  { label: 'Partir en pause',        bg: 'bg-yellow-500 hover:bg-yellow-600' },
    resume: { label: 'Reprendre le travail',   bg: 'bg-blue-500 hover:bg-blue-600' },
    leave:  { label: 'Pointer le départ',      bg: 'bg-red-500 hover:bg-red-600' },
  }

  return (
    <MobileLayout>
      <div className="p-4 space-y-4">

        {/* Clock */}
        <div className="bg-gradient-to-b from-blue-600 to-blue-700 rounded-2xl p-6 text-center text-white">
          <div className="text-5xl font-light tracking-wider">
            {now.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
          </div>
          <div className="text-blue-200 mt-2 text-sm">
            {now.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
          </div>
        </div>

        {/* Hors-ligne banner */}
        {!isOnline && (
          <div className="flex items-center gap-2 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl">
            <WifiOff size={16} className="text-amber-500 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-amber-800">Mode hors-ligne</p>
              <p className="text-xs text-amber-600">Vos pointages seront synchronisés à la reconnexion</p>
            </div>
          </div>
        )}

        {/* File de synchro */}
        {isOnline && offlineQueue.length > 0 && (
          <div className="flex items-center gap-2 px-4 py-3 bg-blue-50 border border-blue-200 rounded-xl">
            <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin flex-shrink-0" />
            <p className="text-sm text-blue-700">
              {syncingQueue ? 'Synchronisation en cours...' : `${offlineQueue.length} pointage${offlineQueue.length > 1 ? 's' : ''} en attente de synchronisation`}
            </p>
          </div>
        )}

        {/* Confirmation rapide */}
        {justPosted && (
          <div className="flex items-center gap-2 px-4 py-3 bg-green-50 border border-green-200 rounded-xl">
            <CheckCircle size={16} className="text-green-500 flex-shrink-0" />
            <p className="text-sm text-green-700 font-medium">
              {EVENT_LABELS[justPosted]} enregistré{!isOnline ? ' (hors-ligne)' : ''}
            </p>
          </div>
        )}

        {/* Géolocalisation toggle */}
        <div className="flex items-center justify-between px-4 py-2.5 bg-white rounded-xl border border-gray-100 shadow-sm">
          <div className="flex items-center gap-2">
            <MapPin size={15} className={geoEnabled ? 'text-blue-500' : 'text-gray-400'} />
            <span className="text-sm text-gray-700">Géolocalisation</span>
            {lastGeo && geoEnabled && (
              <span className="text-xs text-gray-400">{lastGeo.latitude.toFixed(4)}, {lastGeo.longitude.toFixed(4)}</span>
            )}
          </div>
          <button
            onClick={() => setGeoEnabled(v => !v)}
            className={`w-10 h-5 rounded-full relative transition-colors ${geoEnabled ? 'bg-blue-500' : 'bg-gray-300'}`}>
            <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${geoEnabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
          </button>
        </div>

        {/* Action button */}
        {loading ? (
          <div className="text-center py-8 text-gray-400">Chargement...</div>
        ) : nextAction ? (
          <div className="bg-white rounded-2xl shadow-sm p-4 space-y-3">
            <button
              onClick={() => doEvent(nextAction)}
              disabled={posting}
              className={`w-full py-4 rounded-xl font-semibold text-lg text-white transition-all active:scale-95 disabled:opacity-50 ${ACTION_CONFIG[nextAction].bg}`}>
              {posting ? 'Enregistrement...' : ACTION_CONFIG[nextAction].label}
            </button>
            <button
              onClick={() => setShowNote(v => !v)}
              className="w-full text-sm text-gray-500 hover:text-gray-700 text-center">
              {showNote ? 'Masquer la note' : '+ Ajouter une note (optionnel)'}
            </button>
            {showNote && (
              <textarea
                value={note}
                onChange={e => setNote(e.target.value)}
                placeholder="Note..."
                rows={2}
                className="w-full border border-gray-200 rounded-xl p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-300"
              />
            )}
          </div>
        ) : (
          <div className="bg-green-50 rounded-2xl p-4 text-center">
            <div className="text-green-600 font-semibold text-lg">Journée terminée</div>
            <div className="text-green-500 text-sm mt-1">Votre départ a été enregistré</div>
          </div>
        )}

        {/* Today's events */}
        <div>
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Pointages du jour</h2>
          {events.length === 0 ? (
            <div className="bg-white rounded-2xl p-4 text-center text-gray-400 text-sm shadow-sm">
              Aucun pointage enregistré
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
              {events.map((e, i) => (
                <div key={e.id} className={`flex items-center justify-between px-4 py-3 ${i > 0 ? 'border-t border-gray-100' : ''}`}>
                  <div className="flex items-center gap-3">
                    <span className="text-xl">{EVENT_ICONS[e.eventType]}</span>
                    <div>
                      <span className="font-medium text-gray-800">{EVENT_LABELS[e.eventType]}</span>
                      {e.adjustedTime && e.adjustedTime !== formatTime(e.recordedAt) && (
                        <span className="block text-xs text-blue-600">→ {e.adjustedTime} (règle badgeuse)</span>
                      )}
                      {e.note && <span className="block text-xs text-gray-400 italic">{e.note}</span>}
                      {e.latitude && (
                        <span className="block text-xs text-gray-400 flex items-center gap-1">
                          <MapPin size={10} /> Géolocalisé
                        </span>
                      )}
                    </div>
                  </div>
                  <span className="text-gray-600 font-mono text-sm">{formatTime(e.recordedAt)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </MobileLayout>
  )
}
